import { FC, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { MDSProgressBar, MDSProgressBarColor } from "@/mds/components/loading/progress-bar";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds/components/text";

const COUNTDOWN_REFRESH_INTERVAL_MS = 100;
const NOTICE_HEIGHT_PX = 76;
const SECOND_MS = 1_000;

export type FloatingAgentVoiceSessionNoticeState =
  | {
      kind: "canceled";
      countdownStartedAtMs: number;
      countdownDeadlineMs: number;
    }
  | {
      kind: "ending-soon";
      countdownStartedAtMs: number;
      countdownDeadlineMs: number;
    };

/**
 * A compact status card for the two time-sensitive open-mic decisions:
 * recover an Escape cancellation, or renew an expiring session.
 */
export const FloatingAgentVoiceSessionNotice: FC<{
  notice: FloatingAgentVoiceSessionNoticeState;
  hotkeyLabel: string;
}> = ({ notice, hotkeyLabel }) => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    setNowMs(Date.now());
    const interval = setInterval(() => setNowMs(Date.now()), COUNTDOWN_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [notice.countdownDeadlineMs]);

  const countdownDurationMs = Math.max(1, notice.countdownDeadlineMs - notice.countdownStartedAtMs);
  const remainingMs = Math.max(0, notice.countdownDeadlineMs - nowMs);
  const remainingSeconds = Math.ceil(remainingMs / SECOND_MS);
  const isCanceled = notice.kind === "canceled";

  return (
    <StyledNotice role="status" aria-live="polite">
      <StyledNoticeCopy>
        <MDSText variant={MDSTextVariant.TextSm} color={MDSTextColor.Primary}>
          {isCanceled ? "Voice input canceled" : `Ending voice input in ${remainingSeconds}s`}
        </MDSText>
        <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.Secondary}>
          Press <StyledKey>{isCanceled ? "Esc" : hotkeyLabel}</StyledKey> again to{" "}
          {isCanceled ? "undo" : "keep it going"}
        </MDSText>
      </StyledNoticeCopy>
      <MDSProgressBar
        aria-label={
          isCanceled ? "Time left to undo cancellation" : "Time left to renew voice input"
        }
        minValue={0}
        maxValue={countdownDurationMs}
        value={remainingMs}
        color={isCanceled ? MDSProgressBarColor.Emphasis : MDSProgressBarColor.Danger}
        size="small"
        rounded
      />
    </StyledNotice>
  );
};

const StyledNotice = styled.div`
  width: 290px;
  max-width: calc(100vw - 32px);
  height: ${NOTICE_HEIGHT_PX}px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.surface.popover};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  animation: floating-agent-voice-notice-enter 160ms cubic-bezier(0.16, 1, 0.3, 1) both;

  @keyframes floating-agent-voice-notice-enter {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const StyledNoticeCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StyledKey = styled.kbd`
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font: inherit;
  line-height: 1;
`;
